import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Edit, Plus, RefreshCw, Trash2 } from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import AiPromptConfigService from '../../services/aiPromptConfigService';

const EMPTY_FORM = {
  _id: '',
  promptName: '',
  promptContent: '',
  sectionMappingText: '',
};

const mapPromptToForm = (item) => ({
  _id: item._id,
  promptName: item.promptName || '',
  promptContent: item.promptContent || '',
  sectionMappingText: (item.sectionMapping || []).join(', '),
});

const AiPromptConfig = () => {
  const { success, error: showError, toasts, removeToast } = useToast();

  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AiPromptConfigService.getPrompts();
      setPrompts(Array.isArray(res.data) ? res.data : []);
    } catch {
      showError('Failed to load AI prompt configurations.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditorOpen(false);
  };

  const onCreate = () => {
    setForm(EMPTY_FORM);
    setEditorOpen(true);
  };

  const onEdit = (item) => {
    setForm(mapPromptToForm(item));
    setEditorOpen(true);
  };

  const onSave = async () => {
    if (!form.promptName.trim()) {
      showError('Prompt Name is required.');
      return;
    }

    if (!form.promptContent.trim()) {
      showError('Prompt Content is required.');
      return;
    }

    const payload = {
      promptName: form.promptName.trim(),
      promptContent: form.promptContent,
      sectionMapping: form.sectionMappingText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      isActive: prompts.find((p) => p._id === form._id)?.isActive || false,
    };

    setSaving(true);
    try {
      if (form._id) {
        await AiPromptConfigService.updatePrompt(form._id, payload);
        success('Prompt updated.');
      } else {
        await AiPromptConfigService.createPrompt(payload);
        success('Prompt created.');
      }
      resetForm();
      loadPrompts();
    } catch (err) {
      showError(err.message || 'Failed to save prompt.');
    } finally {
      setSaving(false);
    }
  };

  const onActivate = async (item) => {
    try {
      await AiPromptConfigService.activatePrompt(item._id);
      success('Prompt activated for AI engine injection.');
      loadPrompts();
    } catch (err) {
      showError(err.message || 'Activation failed.');
    }
  };

  const onDelete = async (item) => {
    const confirmed = window.confirm(`Delete prompt "${item.promptName}"?`);
    if (!confirmed) return;

    try {
      await AiPromptConfigService.deletePrompt(item._id);
      success('Prompt deleted.');
      loadPrompts();
    } catch (err) {
      showError(err.message || 'Delete failed.');
    }
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="AI Prompt Configuration Screen"
        description="Configure reusable prompts and activate one for dynamic LLM request injection"
        actions={[
          {
            icon: (
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            ),
            variant: 'outline',
            onClick: loadPrompts,
          },
          {
            label: 'Create',
            icon: <Plus size={16} />,
            variant: 'primary',
            onClick: onCreate,
          },
        ]}
      />

      <div className="page-container">
        {editorOpen && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-6">
            <h3 className="text-base font-semibold text-gray-800 mb-4">
              {form._id ? 'Edit Prompt' : 'Create Prompt'}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                  Prompt Name
                </label>
                <input
                  value={form.promptName}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, promptName: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
                  placeholder="Prompt Name"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                  Section Mapping
                </label>
                <input
                  value={form.sectionMappingText}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      sectionMappingText: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
                  placeholder="overview, scope, technical, eligibility, commercial"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                Prompt Content
              </label>
              <textarea
                value={form.promptContent}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    promptContent: e.target.value,
                  }))
                }
                className="w-full border border-gray-300 rounded-lg p-3 text-sm min-h-[160px]"
                placeholder="Write the base AI prompt instructions and section-specific generation rules..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={resetForm}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                {saving ? 'Saving...' : form._id ? 'Edit' : 'Create'}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-3 px-4">Prompt Name</th>
                  <th className="py-3 px-4">Prompt Content</th>
                  <th className="py-3 px-4">Section Mapping</th>
                  <th className="py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {prompts.map((item) => (
                  <tr key={item._id} className="border-b align-top">
                    <td className="py-3 px-4 font-semibold text-gray-900">
                      <div className="flex items-center gap-2">
                        <span>{item.promptName}</span>
                        {item.isActive && (
                          <span className="px-2 py-0.5 text-[10px] rounded-full bg-green-100 text-green-700 font-bold uppercase">
                            Active
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-700 max-w-[420px]">
                      <div className="line-clamp-3">{item.promptContent}</div>
                    </td>
                    <td className="py-3 px-4 text-gray-700">
                      {(item.sectionMapping || []).length
                        ? item.sectionMapping.join(', ')
                        : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          title="Edit"
                          onClick={() => onEdit(item)}
                          className="p-2 rounded-md border border-gray-200 hover:bg-gray-50"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          title="Activate"
                          onClick={() => onActivate(item)}
                          className="p-2 rounded-md border border-gray-200 hover:bg-gray-50"
                        >
                          <CheckCircle2 size={14} />
                        </button>
                        <button
                          title="Delete"
                          onClick={() => onDelete(item)}
                          className="p-2 rounded-md border border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!prompts.length && (
                  <tr>
                    <td colSpan={4} className="py-5 px-4 text-sm text-gray-500">
                      No prompt configurations found. Create one and activate it
                      for AI flow injection.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default AiPromptConfig;
